import axios from 'axios'
import {FeatureConfig} from '@/utils/constants/featuresFlag'
import {attachAuthInterceptor} from '@/core/interceptors/authInterceptor'
import {attachErrorInterceptor} from '@/core/interceptors/errorInterceptor'
import {HTTP_METHODS} from '@/utils/constants/httpMethods'
import {API_VERSION, Constants} from '@/utils/constants/commonConstants'

const createApiClient = baseUrlKey => {
  const baseURL = import.meta.env[FeatureConfig[baseUrlKey]] || import.meta.env[FeatureConfig.apiUrl]
  const client = axios.create({baseURL, timeout: 100000})

  attachAuthInterceptor(client)
  attachErrorInterceptor(client)

  return client
}

const loadMockFromPath = async mockPath => {
  try {
    const response = await fetch(mockPath)
    if (!response.ok) {
      throw new Error(`Failed to fetch mock: ${response.status}`)
    }
    return await response.json()
  } catch (error) {
    console.warn(`[apiClient] Failed to load mock from ${mockPath}:`, error.message)
    return {}
  }
}

const getMockResponse = async config => {
  if (config.mockData) {
    return config.mockData
  }

  if (config.mockPath) {
    return await loadMockFromPath(config.mockPath)
  }

  return {}
}

const shouldUseMock = config => {
  return config.mockData || config.mockPath
}

const buildEndpoint = (config, queryParams) => {
  let endpoint = ''

  if (config.growthbookSdkKey) {
    const sdkValue = import.meta.env[FeatureConfig[config.growthbookSdkKey]]
    endpoint = `/${config.endPoint}/${sdkValue}`
  } else if (config.baseUrlKey) {
    endpoint = `/${config.endPoint}`
  } else {
    const version = config.version || API_VERSION.V1
    endpoint = `${version}/${config.endPoint}`
  }

  if (shouldAppendQueryParams(config, queryParams)) {
    endpoint += `?${queryParams}`
  }

  return endpoint
}

const shouldAppendQueryParams = (config, queryParams) => {
  if (!queryParams) return false

  const method = config.method || HTTP_METHODS.GET
  const isGet = method === HTTP_METHODS.GET
  const isMultipartPost = method === HTTP_METHODS.POST && config.multipart === Constants.Multipart

  return isGet || isMultipartPost || config.appendQueryParams
}

const buildRequestOptions = (config, options, endpoint) => ({
  method: config.method || HTTP_METHODS.GET,
  url: endpoint,
  headers: {
    'Content-Type': config.contentType || 'application/json',
    ...config.headers,
  },
  ...options,
})

const executeRequest = async (config, options, endpoint) => {
  const baseUrlKey = config.baseUrlKey || 'apiUrl'
  const client = createApiClient(baseUrlKey)
  const requestOptions = buildRequestOptions(config, options, endpoint)
  const response = await client(requestOptions)
  return response.data
}

export const request = async (config = {}, options = {}, queryParams = '') => {
  // Early return for mocks - prevents any real API calls
  if (shouldUseMock(config)) {
    console.warn(`[apiClient] Using mock data for endpoint: ${config?.endPoint}`)
    return await getMockResponse(config)
  }

  const endpoint = buildEndpoint(config, queryParams)

  try {
    return await executeRequest(config, options, endpoint)
  } catch (error) {
    console.error('[apiClient] Request failed:', error.message)
    throw error
  }
}
